import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import {
  Coins,
  TimerReset,
} from 'lucide-react'

import { pointActivityService } from '../services/pointActivityService'
import { useAuthStore } from '../store/authStore'
import {
  getDailyReviewProgress,
  PointProgressRing,
} from './PointProgressRing'
import { getPointResetCountdown } from '../utils/pointCountdown'
import {
  maskPointIdentifier,
  pointDebug,
  summarizePointResponse,
} from '../utils/pointDebug'

interface WalletBalanceProps {
  accountId?: string
  coinsTotal?: number
  pointsTotal?: number
  pointsResetAt?: string | null
}

export default function WalletBalance({
  accountId,
  coinsTotal = 0,
  pointsTotal = 0,
  pointsResetAt,
}: WalletBalanceProps) {
  const status = useAuthStore(
    (state) =>
      accountId
        ? state.pointStatusByAccount[accountId]
        : undefined
  )

  const liveActiveSeconds = useAuthStore(
    (state) =>
      accountId
        ? state.pointLiveActiveSecondsByAccount[accountId] ?? status?.dailyReview.activeSeconds ?? 0
        : 0
  )

  const pointError = useAuthStore(
    (state) =>
      accountId
        ? state.pointErrorByAccount[accountId] ?? null
        : null
  )

  const setPointStatus = useAuthStore(
    (state) => state.setPointStatus
  )

  const setPointError = useAuthStore(
    (state) => state.setPointError
  )

  const updateAccountWallet = useAuthStore(
    (state) => state.updateAccountWallet
  )
  const [loading, setLoading] = useState(false)
  const refresh = useCallback(async () => {
    if (!accountId) return

    setLoading(true)
    try {
      const response =
        await pointActivityService.getStatus(
          accountId
        )

      setPointStatus(
        accountId,
        response
      )

      updateAccountWallet(
        accountId,
        {
          pointsTotal:
            response.wallet.pointsTotal,
          coinsTotal:
            response.wallet.coinsTotal,
          pointsResetAt:
            response.wallet.pointsResetAt,
          pointsCycleStartedAt:
            response.wallet.pointsCycleStartedAt,
        }
      )
    } catch (err) {
      setPointError(
        accountId,
        err instanceof Error
          ? err.message
          : 'Unable to refresh Daily Review status.'
      )
    } finally {
      setLoading(false)
    }
  }, [
    accountId,
    setPointError,
    setPointStatus,
    updateAccountWallet,
  ])

  const [nowMs, setNowMs] = useState(Date.now())
  const resetRefreshInFlightRef = useRef(false)

  useEffect(() => {
    const intervalId = window.setInterval(
      () => setNowMs(Date.now()),
      60 * 1000
    )

    return () => window.clearInterval(intervalId)
  }, [])

  const effectivePoints =
    status?.wallet.pointsTotal ?? pointsTotal

  const effectiveCoins =
    status?.wallet.coinsTotal ?? coinsTotal

  const resetValue =
    status?.wallet.pointsResetAt ??
    pointsResetAt ??
    null

  const countdown = getPointResetCountdown(
    resetValue,
    nowMs
  )
  const reviewProgress = getDailyReviewProgress(status, liveActiveSeconds)

  useEffect(() => {
    pointDebug('ui.wallet_render_state', {
      accountId: maskPointIdentifier(accountId),
      propPointsTotal: pointsTotal,
      effectivePoints,
      effectiveCoins,
      loading,
      pointError,
      resetValue,
      liveActiveSeconds,
      progress: {
        fraction: reviewProgress.fraction,
        percent: reviewProgress.percent,
        pending: reviewProgress.pending,
        label: reviewProgress.label,
      },
      point: summarizePointResponse(status),
    })
  }, [
    accountId,
    effectiveCoins,
    effectivePoints,
    loading,
    pointError,
    pointsTotal,
    resetValue,
    reviewProgress.fraction,
    reviewProgress.label,
    reviewProgress.pending,
    reviewProgress.percent,
    liveActiveSeconds,
    status,
  ])

  useEffect(() => {
    if (
      !resetValue ||
      countdown.remainingSeconds !== 0 ||
      resetRefreshInFlightRef.current
    ) {
      return
    }

    // Retry on a later minute tick if the authoritative refresh fails. A
    // permanent one-shot guard would leave the UI stuck on "Resetting now"
    // after a transient network error.
    resetRefreshInFlightRef.current = true

    void refresh().finally(() => {
      resetRefreshInFlightRef.current = false
    })
  }, [
    countdown.remainingSeconds,
    refresh,
    resetValue,
    nowMs,
  ])

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <div
        className="rounded-2xl border border-amber-500/25 bg-amber-500/10 p-4 text-amber-100 shadow-sm"
        title="Coins do not expire"
      >
        <div className="flex items-center justify-between text-xs uppercase tracking-[0.2em] text-amber-300/80">
          <span>Coins</span>
          <Coins className="h-4 w-4" />
        </div>

        <div className="mt-3 text-2xl font-bold tabular-nums">
          {effectiveCoins.toLocaleString()}
        </div>

        <div className="mt-2 text-xs text-amber-200/70">
          Do not expire
        </div>
      </div>

      <div
        className="rounded-2xl border border-violet-500/25 bg-violet-500/10 p-4 text-violet-100 shadow-sm"
        title={
          resetValue
            ? `Points reset ${new Date(resetValue).toLocaleString()}`
            : 'Earn Points to start a Point cycle'
        }
      >
        <div className="flex items-center justify-between text-xs uppercase tracking-[0.2em] text-violet-300/80">
          <span>Points</span>
            <PointProgressRing
              status={status}
              activeSecondsOverride={liveActiveSeconds}
              size={20}
            />
        </div>

        <div className="mt-3 text-2xl font-bold tabular-nums">
          {loading
            ? '…'
            : effectivePoints.toLocaleString()}
        </div>

        <div
          className={
            pointError
              ? 'mt-2 text-xs text-rose-300'
              : 'mt-2 text-xs text-violet-200/80'
          }
          aria-live="polite"
        >
          {pointError
            ? `Daily Review tracking error: ${pointError}`
            : reviewProgress.label}
        </div>

        <div className="mt-2 flex items-center gap-2 text-xs text-violet-200/80">
          <TimerReset className="h-3.5 w-3.5" />
          <span>{countdown.label}</span>
        </div>
      </div>
    </div>
  )
}
